import { useState } from 'react';
import { useAppsMenuStore } from '../context/AppsMenuContext';
import { useAppSettings } from './useAppSettings';
import useApi from '../api/useApi';
import { AppsMenuHtml } from '../types/AppsMenuHtml';

/**
 * Custom hook for loading base data (apps menu)
 * Products are now loaded on-demand using QueryProductMetaData in ProductsList
 */
const useLoadBaseData = () => {
    const { getApiData } = useApi();
    const { setAppsMenu, setIsLoading: setAppsMenuLoading } = useAppsMenuStore();
    const { settings: appSettings } = useAppSettings();
    const [dataLoadingState, setDataLoadingState] = useState<{
        baseDataLoaded: boolean;
    }>({ baseDataLoaded: false });

    /**
     * Fetches the apps menu HTML from the API
     * This loads once and is cached in context
     */
    const getAppsMenu = async (): Promise<AppsMenuHtml | null> => {
        try {
            const apiUrl = appSettings?.api;
            if (!apiUrl) {
                console.warn('[useLoadBaseData] API URL not configured, skipping apps menu load');
                return null;
            }

            console.log('[useLoadBaseData] Fetching apps menu...');
            setAppsMenuLoading(true);

            const response = await getApiData({
                endpoint: `${apiUrl}/GetAppsDataHtml`,
            });

            const appsMenuHtml = response as AppsMenuHtml;
            console.log(`[useLoadBaseData] Apps menu loaded successfully`);
            return appsMenuHtml;
        } catch (error) {
            console.error('[useLoadBaseData] Error fetching apps menu (non-critical):', error);
            return null;
        } finally {
            setAppsMenuLoading(false);
        }
    };

    /**
     * Loads all base data required for the application
     * Now only loads apps menu - products are loaded on-demand via QueryProductMetaData
     */
    const loadBaseData = async () => {
        try {
            setDataLoadingState({ baseDataLoaded: false });
            console.log('[useLoadBaseData] Starting base data load...');

            // Fetch apps menu (non-blocking - failures won't stop app load)
            const appsMenuData = await getAppsMenu();
            if (appsMenuData) {
                setAppsMenu(appsMenuData);
            }

            setDataLoadingState({ baseDataLoaded: true });
            console.log('[useLoadBaseData] Base data loaded successfully');
        } catch (error) {
            console.error('[useLoadBaseData] Error loading base data:', error);
            setDataLoadingState({ baseDataLoaded: false });
            return { success: false, error };
        }
    };

    return {
        loadBaseData,
        isBaseDataLoaded: dataLoadingState.baseDataLoaded,
        setBaseDataLoaded: (loaded: boolean) => setDataLoadingState({ baseDataLoaded: loaded }),
    };
};

export default useLoadBaseData;
